// =============================================================================
// vehicle-icon — pakai PNG asset di src/assets/markers/* untuk render marker
// vehicle di map. Vite static import → bundle URL.
//
// Mapping shape × status → URL PNG. Fallback ke default.png kalau tidak ada
// variant spesifik (mis. truck/motorcycle tidak punya _alarm.png → pakai _move).
//
// IconLayer dipakai dengan per-feature `getIcon` callback yang return
// {url, width, height, anchorX, anchorY, mask: false}. PNG sudah pre-colored,
// jadi `mask: false` (jangan tint pakai getColor).
// =============================================================================

// --- Static imports (Vite bundles ke URL) ---
import carMove    from '@/assets/markers/car_move.png';
import carStop    from '@/assets/markers/car_stop.png';
import carOffline from '@/assets/markers/car_offline.png';
import carAlarm   from '@/assets/markers/car_alarm.png';
import truckMove    from '@/assets/markers/truck_move.png';
import truckStop    from '@/assets/markers/truck_stop.png';
import truckOffline from '@/assets/markers/truck_offline.png';
import truckIdle    from '@/assets/markers/truck_idle.png';
import motoMove    from '@/assets/markers/motorcycle_move.png';
import motoStop    from '@/assets/markers/motorcycle_stop.png';
import motoOffline from '@/assets/markers/motorcycle_offline.png';
import defaultPng  from '@/assets/markers/default.png';

// =============================================================================
// Type defs
// =============================================================================

export type VehicleStatus = 'move' | 'park' | 'offline' | 'alarm';
export type VehicleShape  = 'car' | 'truck' | 'motorcycle' | 'person' | 'generic';

// =============================================================================
// object_type → VehicleShape mapping (case-insensitive).
// =============================================================================

export function objectTypeToShape(t: string | undefined | null): VehicleShape {
  switch ((t ?? '').toLowerCase()) {
    case 'car':
    case 'mobil':
      return 'car';
    case 'truck':
    case 'truk':
      return 'truck';
    case 'motorcycle':
    case 'motor':
    case 'bike':
      return 'motorcycle';
    case 'orang':
    case 'person':
    case 'people':
      return 'person';
    default:
      return 'generic';
  }
}

// =============================================================================
// (shape, status) → PNG URL mapping.
// Untuk variant yang tidak tersedia (mis. truck-alarm), fallback ke
// variant terdekat — biasanya _move (vehicle dalam keadaan aktif).
// =============================================================================

const ICON_URLS: Record<string, string> = {
  // Car — lengkap 4 status
  'car-move':     carMove,
  'car-park':     carStop,
  'car-offline':  carOffline,
  'car-alarm':    carAlarm,

  // Truck — tidak ada _alarm; fallback ke _move
  'truck-move':    truckMove,
  'truck-park':    truckStop,
  'truck-offline': truckOffline,
  'truck-alarm':   truckMove,
  // bonus: idle → kalau caller pakai mapping berbeda
  'truck-idle':    truckIdle,

  // Motorcycle — tidak ada _alarm; fallback ke _move
  'motorcycle-move':    motoMove,
  'motorcycle-park':    motoStop,
  'motorcycle-offline': motoOffline,
  'motorcycle-alarm':   motoMove,

  // Person, generic → semua status pakai default.png
  'person-move':     defaultPng,
  'person-park':     defaultPng,
  'person-offline':  defaultPng,
  'person-alarm':    defaultPng,
  'generic-move':    defaultPng,
  'generic-park':    defaultPng,
  'generic-offline': defaultPng,
  'generic-alarm':   defaultPng,
};

export function getVehicleIconUrl(shape: VehicleShape, status: VehicleStatus): string {
  return ICON_URLS[`${shape}-${status}`] ?? defaultPng;
}

// =============================================================================
// IconLayer per-feature icon spec — return shape ini dari getIcon callback.
// width/height = source PNG; deck.gl scale ke `getSize` saat render.
// anchorX/Y = center supaya marker tepat di koordinat (top-down view icons).
// mask=false: PNG sudah pre-colored, jangan tint.
// =============================================================================

export interface IconSpec {
  url: string;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  mask: boolean;
  id: string; // unique key untuk cache deck.gl (= "shape-status")
}

const ICON_SIZE = 92;

export function makeIconSpec(shape: VehicleShape, status: VehicleStatus): IconSpec {
  return {
    id: `${shape}-${status}`,
    url: getVehicleIconUrl(shape, status),
    width: ICON_SIZE,
    height: ICON_SIZE,
    anchorX: ICON_SIZE / 2,
    anchorY: ICON_SIZE / 2,
    mask: false,
  };
}
